// imports
import { useEffect, useState, useMemo } from 'react';

// local imports
import { addError } from '@shared/utils/logger';
import { ConfirmedSearchPayload } from './useRegionalHotels';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useStandardHotelRoomPriceMapping } from './useListingPriceMapping';
import { useAccommodationsConsolePropertiesV2Query, Maybe } from '@graphql/generated';
import { useTravelWhiteLabelCurrency } from '@shared/hooks/useTravelWhiteLabelCurrency';
import { StandardHotelPricingValues } from '../AccommodationsConsole.types';
import { useExtendedAccommodationSearchMetadata } from '@shared/hooks/useSearchAccommodationsQueryMetadataPayload';

interface UseBackgroundPricingRefreshArgs
  extends Readonly<{
    eventHandle?: Maybe<string>;
    isInitialLoad: boolean;
    confirmedSearchPayload: ConfirmedSearchPayload;
    isFetchingListings: boolean;
  }> {}

interface BackgroundPricingRefreshResult
  extends Readonly<{
    isLoading: boolean;
    pricingMap: Readonly<Map<string, StandardHotelPricingValues | null>>;
  }> {}

export const useBackgroundPricingRefresh = (args: UseBackgroundPricingRefreshArgs): BackgroundPricingRefreshResult => {
  const { eventHandle, confirmedSearchPayload, isFetchingListings, isInitialLoad } = args;

  const { isPreparingSearchMetadata, searchMetadata } = useExtendedAccommodationSearchMetadata();

  const [isFeatureEnabled] = useIsFeatureEnabled('accommodationsBatchPricingRefresh');
  const [pricingMap, setPricingMap] = useState<Map<string, StandardHotelPricingValues | null>>(new Map());

  const currencyCode = useTravelWhiteLabelCurrency();
  const mapHotelRoomPrice = useStandardHotelRoomPriceMapping();

  const payload = useMemo(() => {
    if (!eventHandle || isPreparingSearchMetadata || (!isInitialLoad && !confirmedSearchPayload)) {
      return null;
    }

    const customSearchParams: Record<string, unknown> = {
      currencyCode,
      metadata: searchMetadata,
      useRealtimePricing: true,
    };

    if (confirmedSearchPayload?.location) {
      customSearchParams.location = confirmedSearchPayload.location;
    }

    if (confirmedSearchPayload?.occupancies) {
      customSearchParams.occupancies = confirmedSearchPayload.occupancies;
    }

    if (confirmedSearchPayload?.searchDates) {
      customSearchParams.stayDates = {
        checkIn: {
          low: confirmedSearchPayload.searchDates.checkin,
          high: confirmedSearchPayload.searchDates.checkin,
        },
        checkOut: {
          low: confirmedSearchPayload.searchDates.checkout,
          high: confirmedSearchPayload.searchDates.checkout,
        },
      };
    }

    return {
      eventHandle,
      customSearchParams,
    };
  }, [eventHandle, currencyCode, isPreparingSearchMetadata, searchMetadata, confirmedSearchPayload, isInitialLoad]);

  const shouldSkipQuery = !isFeatureEnabled || !payload || isFetchingListings;

  const { data, error, loading } = useAccommodationsConsolePropertiesV2Query({
    batchMode: 'fast',
    skip: shouldSkipQuery,
    fetchPolicy: 'network-only',
    variables: { payload: payload! },
  });

  useEffect(() => {
    const properties = data?.searchExtendedAccommodationProperties?.properties;
    if (!properties) {
      return;
    }

    setPricingMap(prev => {
      const updated = new Map(prev);
      for (const property of properties) {
        const { joyPlaceId, pricingInfo } = property;
        if (joyPlaceId) {
          updated.set(joyPlaceId, pricingInfo ? mapHotelRoomPrice(pricingInfo) : null);
        }
      }
      return updated;
    });
  }, [data, mapHotelRoomPrice]);

  useEffect(() => {
    if (error) {
      addError(error, {
        errorMessage: error.message,
        context: 'useBackgroundPricingRefresh',
        source: 'Background pricing refresh failed (AccommodationsConsole)',
      });
    }
  }, [error]);

  return {
    pricingMap,
    isLoading: loading,
  };
};
